if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      constructor() {
        super();
        this.variantSelects = this.querySelector("variant-radios");
        this.submitButton = this.querySelector('[type="submit"]');
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;

      /** Section id used in ?section_id= and shopify-section-{id} */
      getResolvedSectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      /** Root node that contains all buy/quantity UI for this product instance */
      getQuantityScopeRoot() {
        if (this.dataset.originalSection) {
          const modalRoot = this.closest('[id^="QuickAddInfo-"]');
          if (modalRoot) return modalRoot;
        }
        return document.getElementById(
          `shopify-section-${this.getResolvedSectionId()}`,
        );
      }

      getQuantityForms() {
        const root = this.getQuantityScopeRoot();
        if (!root) return [];
        return Array.from(root.querySelectorAll(".product-form__quantity"));
      }

      getPrimaryQuantityInput() {
        const forms = this.getQuantityForms();
        return forms.length ? forms[0].querySelector(".quantity__input") : null;
      }

      getVariantIdInput() {
        const root = this.getQuantityScopeRoot();
        if (!root) return null;
        return (
          root.querySelector(
            'form[data-type="add-to-cart-form"] input[name="id"]:not([disabled])',
          ) || root.querySelector('input.product-variant-id[name="id"]')
        );
      }

      connectedCallback() {
        this.input = this.getPrimaryQuantityInput();
        this.currentVariant = this.getVariantIdInput();
        this.quantityForms = this.getQuantityForms();

        if (!this.input || !this.quantityForms.length) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          (event) => {
            const sectionId = this.getResolvedSectionId();
            if (event.data.sectionId !== sectionId) return;
            this.updateQuantityRules(event.data.sectionId, event.data.html);
            this.setQuantityBoundries();
          },
        );
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      setQuantityBoundries() {
        const primary = this.getPrimaryQuantityInput();
        if (!primary) return;

        const data = {
          cartQuantity: primary.dataset.cartQuantity
            ? parseInt(primary.dataset.cartQuantity, 10)
            : 0,
          min: primary.dataset.min ? parseInt(primary.dataset.min, 10) : 1,
          max: primary.dataset.max ? parseInt(primary.dataset.max, 10) : null,
          step: primary.step ? parseInt(primary.step, 10) : 1,
        };

        let min = data.min;
        const max =
          data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.getQuantityForms().forEach((form) => {
          const input = form.querySelector(".quantity__input");
          if (!input) return;
          input.min = min;
          input.max = max;
          input.value = min;
        });
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const variantInput = this.getVariantIdInput();
        if (!variantInput || !variantInput.value) return;

        const scopeRoot = this.getQuantityScopeRoot();
        let quantityLoadingOverlay = scopeRoot?.querySelector(
          ".quantity__rules-cart .loading-overlay",
        );
        if (quantityLoadingOverlay) {
          quantityLoadingOverlay.classList.remove("hidden");
        }

        const sectionParam = this.getResolvedSectionId();
        fetch(
          `${this.dataset.url}?variant=${variantInput.value}&section_id=${sectionParam}`,
        )
          .then((response) => {
            return response.text();
          })
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            this.updateQuantityRules(sectionParam, html);
            this.setQuantityBoundries();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (quantityLoadingOverlay) {
              quantityLoadingOverlay.classList.add("hidden");
            }
          });
      }

      syncQuantityFormFields(destForm, sourceForm) {
        const destInput = destForm.querySelector(".quantity__input");
        const sourceInput = sourceForm.querySelector(".quantity__input");
        if (!destInput || !sourceInput) return;

        const attributes = [
          "data-cart-quantity",
          "data-min",
          "data-max",
          "step",
        ];
        for (const attribute of attributes) {
          const valueUpdated = sourceInput.getAttribute(attribute);
          if (valueUpdated !== null)
            destInput.setAttribute(attribute, valueUpdated);
        }

        const destLabels = destForm.querySelectorAll(".quantity__label");
        const sourceLabels = sourceForm.querySelectorAll(".quantity__label");
        destLabels.forEach((destLabel, index) => {
          const src = sourceLabels[index];
          if (src) destLabel.innerHTML = src.innerHTML;
        });

        const destRules = destForm.querySelector(".quantity__rules");
        const sourceRules = sourceForm.querySelector(".quantity__rules");
        if (destRules && sourceRules) {
          destRules.innerHTML = sourceRules.innerHTML;
        }
      }

      updateQuantityRules(sectionId, html) {
        let sourceRoot = html.getElementById(
          `shopify-section-${sectionId}`,
        );
        if (!sourceRoot) {
          sourceRoot =
            html.getElementById(`MainProduct-${sectionId}`) || html.body;
        }

        const sourceForms = sourceRoot.querySelectorAll(
          ".product-form__quantity",
        );
        if (!sourceForms.length) return;

        const destForms = this.getQuantityScopeRoot()?.querySelectorAll(
          ".product-form__quantity",
        );
        if (!destForms || !sourceForms.length) return;

        const n = Math.min(sourceForms.length, destForms.length);
        for (let i = 0; i < n; i++) {
          this.syncQuantityFormFields(destForms[i], sourceForms[i]);
        }

        this.input = this.getPrimaryQuantityInput();
        this.currentVariant = this.getVariantIdInput();
      }
    },
  );
}
